import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { getAppInsights } from '../../infrastructure/Logging/TelemetryService';
import { toast } from 'react-toastify';
import { saveErrorPayload } from './errorPayloadStorage';

type Options = {
  timeoutMs?: number; // time to consider render stalled
  minContentSelector?: string; // selector to check for content presence
  autoReload?: boolean; // whether to trigger reload automatically
};

const buildErrorPageUrl = (originPath?: string) => {
  if (!originPath) return '/error';
  const encoded = encodeURIComponent(originPath);
  return `/error?from=${encoded}`;
};

// Detects cases where route changes leave a blank screen (no content rendered)
export function RenderStallDetector({
  timeoutMs = 6000,
  minContentSelector = '#root *',
  autoReload = true,
}: Options) {
  const location = useLocation();
  const timerRef = useRef<number | null>(null);

  useEffect(() => {
    // Clear previous timer
    if (timerRef.current) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }

    // Start a new stall check on navigation
    timerRef.current = window.setTimeout(() => {
      const hasContent = !!document.querySelector(minContentSelector);
      if (!hasContent) {
        try {
          const app = getAppInsights();
          app?.trackEvent({ name: 'render-stall-detected', properties: { path: location.pathname } });
        } catch {}

        if (autoReload) {
          // Show friendly toast before reload/redirect
          toast.error('¡Uy! Ocurrió un error inesperado. Te llevamos a la página de error.');
          // Attempt a soft redirect to an error page
          const originPath = `${location.pathname}${location.search}${location.hash}`;
          saveErrorPayload({
            message: 'La página tardó demasiado en cargar contenido.',
            originPath,
            source: 'render-stall',
            metadata: { timeoutMs },
          });
          window.location.assign(buildErrorPageUrl(originPath));
        }
      }
    }, timeoutMs);

    return () => {
      if (timerRef.current) {
        window.clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [location.pathname, location.search, location.hash, timeoutMs, minContentSelector, autoReload]);

  return null;
}

export default RenderStallDetector;
